/** Finite world-space surface probes, not solid classification or a fit verdict. */
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Material } from '@babylonjs/core/Materials/material';
import type { TraceScene } from 'trace-viewer/scene';
import { resolveUniqueByName } from './agent-helpers';

type V = [number, number, number];
type Triangle = [V, V, V];
interface Bounds { min: V; max: V }
interface Surface extends Bounds { path: string; triangles: Triangle[] }
interface Hit { object: string; distance: number; point: V; normal: V }
const sub = (a: V, b: V): V => [a[0]-b[0], a[1]-b[1], a[2]-b[2]];
const cross = (a: V, b: V): V => [a[1]*b[2]-a[2]*b[1], a[2]*b[0]-a[0]*b[2], a[0]*b[1]-a[1]*b[0]];
const dot = (a: V, b: V) => a[0]*b[0]+a[1]*b[1]+a[2]*b[2];
const vector = (v: unknown): v is V => Array.isArray(v) && v.length === 3 && v.every(n=>typeof n==='number' && Number.isFinite(n));
const TOLERANCE = 1e-7;

function intersectsBounds(origin: V, direction: V, length: number, surface: Bounds): boolean {
    let near = 0, far = length;
    for (let k=0;k<3;k++) {
        if (direction[k]===0) { if (origin[k]<surface.min[k]-TOLERANCE || origin[k]>surface.max[k]+TOLERANCE) return false; }
        else {
            const a=(surface.min[k]-TOLERANCE-origin[k])/direction[k], b=(surface.max[k]+TOLERANCE-origin[k])/direction[k];
            near=Math.max(near,Math.min(a,b)); far=Math.min(far,Math.max(a,b));
            if (near>far) return false;
        }
    }
    return true;
}

export function raycastGeometry(input: Record<string, unknown>, scene: TraceScene): string {
    const refs=input.objects, probes=input.rays, maxHits=input.max_hits??4;
    if (!Array.isArray(refs)||!refs.length||refs.length>128||refs.some(v=>typeof v!=='string'||!v.trim())) throw new Error('objects must contain 1–128 names/paths; descendants are included.');
    if (!Array.isArray(probes)||!probes.length||probes.length>8) throw new Error('rays must contain 1–8 world-space probes.');
    if (!Number.isSafeInteger(maxHits)||(maxHits as number)<1||(maxHits as number)>16) throw new Error('max_hits must be 1–16.');
    const rays=probes.map((value: unknown)=>{
        if (!value||typeof value!=='object') throw new Error('Each ray needs origin, direction and length.');
        const r=value as Record<string,unknown>;
        if (!vector(r.origin)||!vector(r.direction)||typeof r.length!=='number'||!Number.isFinite(r.length)||r.length<=0) throw new Error('Ray vectors must be finite triples and length must be positive and finite.');
        const magnitude=Math.hypot(...r.direction);
        if (!Number.isFinite(magnitude)||magnitude===0) throw new Error('Ray direction must be nonzero.');
        return {origin:r.origin,direction:r.direction.map(n=>n/magnitude) as V,length:r.length};
    });
    const roots=new Set((refs as string[]).map(ref=>resolveUniqueByName(scene,ref,'surface probe')));
    const objects=scene.getSceneObjects().filter(o=>{for(let p:typeof o|null=o;p;p=p.getParent())if(roots.has(p))return true;return false;});
    if (objects.length>4096) throw new Error('Selection exceeds 4096 objects; select a smaller assembly.');
    const surfaces:Surface[]=[], skipped:{object:string;reason:string}[]=[];
    let triangles=0, vertices=0, scannedVertices=0, culledMeshes=0;
    // Bounds come from the current CPU positions, never potentially stale render bounds.
    // Shared geometry is scanned once; instance transforms are still independent.
    const localBounds=new Map<ArrayLike<number>,Bounds|null>();
    for (const object of objects) {
        const path=object.getPath();
        const meshes=object.getOwnedMeshes().filter(m=>m.isEnabled()&&m.isVisible&&m.visibility>0&&m.getTotalVertices()>0);
        if (object.src&&object.active&&!meshes.length) skipped.push({object:path,reason:'No active loaded geometry.'});
        for (const mesh of meshes) {
            const materials=mesh.subMeshes?.map(s=>s.getMaterial())??[mesh.material];
            if (mesh.skeleton||mesh.morphTargetManager||('thinInstanceCount' in mesh&&Number(mesh.thinInstanceCount)>0)) {skipped.push({object:path,reason:'Skinned, morphed or thin-instance geometry is unsupported.'});continue;}
            if (materials.some(m=>m&&![Material.TriangleFillMode,Material.WireFrameFillMode,Material.PointFillMode].includes(('overrideRenderingFillMode' in mesh&&typeof mesh.overrideRenderingFillMode==='number'?mesh.overrideRenderingFillMode:m.fillMode)))) {skipped.push({object:path,reason:'Non-triangle topology is unsupported.'});continue;}
            const positions=mesh.getVerticesData('position'), indices=mesh.getIndices();
            if (!positions?.length||positions.length%3||!indices?.length||indices.length%3) {skipped.push({object:path,reason:'CPU indexed triangles unavailable or malformed.'});continue;}
            if (!localBounds.has(positions)) {
                scannedVertices+=positions.length/3;
                if (scannedVertices>300000) throw new Error('Selection exceeds 300000 unique vertices for bounds inspection; inspect a smaller assembly.');
                const min:V=[Infinity,Infinity,Infinity],max:V=[-Infinity,-Infinity,-Infinity];
                let finite=true;
                for(let i=0;i<positions.length;i++) {
                    const value=positions[i],k=i%3;
                    if(!Number.isFinite(value)){finite=false;break;}
                    min[k]=Math.min(min[k],value);max[k]=Math.max(max[k],value);
                }
                localBounds.set(positions,finite?{min,max}:null);
            }
            const local=localBounds.get(positions);
            if(!local){skipped.push({object:path,reason:'Non-finite local geometry.'});continue;}
            const world=mesh.computeWorldMatrix(true);
            const broad:Bounds={min:[Infinity,Infinity,Infinity],max:[-Infinity,-Infinity,-Infinity]};
            for(let corner=0;corner<8;corner++){
                const point=Vector3.TransformCoordinates(new Vector3(...local.min.map((v,k)=>(corner&(1<<k))?local.max[k]:v) as V),world).asArray();
                for(let k=0;k<3;k++){broad.min[k]=Math.min(broad.min[k],point[k]);broad.max[k]=Math.max(broad.max[k],point[k]);}
            }
            if([...broad.min,...broad.max].some(n=>!Number.isFinite(n))){skipped.push({object:path,reason:'Non-finite world geometry.'});continue;}
            if(!rays.some(ray=>intersectsBounds(ray.origin,ray.direction,ray.length,broad))){culledMeshes++;continue;}
            vertices+=positions.length/3; triangles+=indices.length/3;
            if (vertices>300000||triangles>100000) throw new Error('Ray candidates exceed 300000 vertices or 100000 triangles; use shorter rays or inspect a smaller assembly.');
            const points:V[]=[];
            const min:V=[Infinity,Infinity,Infinity],max:V=[-Infinity,-Infinity,-Infinity];
            for (let i=0;i<positions.length;i+=3) {
                const p=Vector3.TransformCoordinates(Vector3.FromArray(positions,i),world);points.push([p.x,p.y,p.z]);
                for (let k=0;k<3;k++) {min[k]=Math.min(min[k],points.at(-1)![k]);max[k]=Math.max(max[k],points.at(-1)![k]);}
            }
            if (points.some(p=>p.some(n=>!Number.isFinite(n)))) {skipped.push({object:path,reason:'Non-finite world geometry.'});continue;}
            const faces:Triangle[]=[];
            for (let i=0;i<indices.length;i+=3) {
                const a=points[indices[i]],b=points[indices[i+1]],c=points[indices[i+2]];
                if (!a||!b||!c) throw new Error(`Invalid triangle indices in ${path}.`);
                faces.push([a,b,c]);
            }
            surfaces.push({path,triangles:faces,min,max});
        }
    }
    const results=rays.map(ray=>{
        const hits:Hit[]=[];
        for (const surface of surfaces) {
            if (!intersectsBounds(ray.origin,ray.direction,ray.length,surface)) continue;
            for (const [a,b,c] of surface.triangles) {
                const e1=sub(b,a),e2=sub(c,a),normal=cross(e1,e2),area=Math.hypot(...normal);
                if (!Number.isFinite(area)) throw new Error('Geometry exceeds the numerical range for surface probes.');
                if (area===0) continue;
                const h=cross(ray.direction,e2),det=dot(e1,h);
                // Coplanar/tangential probes are explicitly outside this tool's evidence.
                if (Math.abs(det)<=area*1e-10) continue;
                const s=sub(ray.origin,a),u=dot(s,h)/det,q=cross(s,e1),v=dot(ray.direction,q)/det;
                if (!Number.isFinite(u)||!Number.isFinite(v)) throw new Error('Ray calculation exceeds numerical range; use closer origins.');
                if (u < -1e-9||v < -1e-9||u+v > 1+1e-9) continue;
                const distance=dot(e2,q)/det;
                if (!Number.isFinite(distance)) throw new Error('Ray calculation exceeds numerical range; use closer origins.');
                if (distance < -TOLERANCE||distance > ray.length+TOLERANCE) continue;
                const d=Math.min(ray.length,Math.max(0,distance));
                hits.push({object:surface.path,distance:d,point:ray.origin.map((n,k)=>n+d*ray.direction[k]) as V,normal:normal.map(n=>n/area) as V});
            }
        }
        hits.sort((a,b)=>a.distance-b.distance||a.object.localeCompare(b.object));
        const distinct:Hit[]=[],last=new Map<string,number>();
        for (const hit of hits) {
            const previous=last.get(hit.object);
            if (previous!==undefined&&hit.distance-previous<=TOLERANCE) continue;
            last.set(hit.object,hit.distance);distinct.push(hit);
        }
        return {...ray,hitCount:distinct.length,hits:distinct.slice(0,maxHits as number),omittedHits:Math.max(0,distinct.length-(maxHits as number))};
    });
    const result=JSON.stringify({method:'Double-sided intersections with loaded indexed triangles in scene world units. Normals follow geometric winding, not an inside/outside classification. Endpoint tolerance 1e-7 units; same-owner hits within that distance merge; coincident different owners remain distinct.',interpretation:'Only these finite rays and selected objects were checked. No hit is not a coverage or clearance certificate: tangencies/coplanar faces, skipped geometry and unsampled areas remain unresolved. Material alpha/textures/culling are ignored; this is geometric exposure, not rendered visibility. No scene changes.',triangles,vertices,scannedVertices,culledMeshes,skipped,results});
    if (result.length>7500) throw new Error('Probe output exceeds 7500 characters. Use fewer rays/objects or smaller max_hits; no results were silently dropped.');
    return result;
}
